import { ButtonHTMLAttributes, forwardRef } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

const cssPerColorProp: { [T: string]: string } = {
  primary: `
    color: #ffffff;
    background: var(--wg-primary);

    &:disabled, &[disabled] {
      color: #ffffff;
      background: #6e7e90;
      border: 1px solid #6e7e90;
    }

    &:hover:not([disabled]){
      --tw-bg-opacity: 1;
      background-color: #1E429F;
    }
    &:focus:not([disabled]) {
    --tw-ring-offset-width: 2px;
    --tw-ring-opacity: 1;
    --tw-ring-color: #1a56dbb3;
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--wg-primary);
    box-shadow: var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow,0 0 #0000);
    }
  `,
  secondary: `
    --tw-text-opacity: 1;
    color: rgb(17 24 39 / var(--tw-text-opacity));
    background: #FFFFFF;
    --tw-border-opacity: 1;
    border: 1px solid;
    border-color: rgb(209 213 219 / var(--tw-border-opacity));

    &:disabled, &[disabled] {
      color: #61686f;
      background: #f3f3f3;
      border: 1px solid #f3f3f3;
    }


     &:hover:not([disabled]){
      --tw-bg-opacity: 1;
      background-color: #F9FAFB;
    }
    &:focus:not([disabled]) {
    --tw-ring-offset-width: 2px;
    --tw-ring-opacity: 1;
    --tw-ring-color: #1a56dbb3;
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--wg-primary);
    box-shadow: var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow,0 0 #0000);

    }
  `,
  tertiary: `
    color: var(--wg-primary);
    border: 1px solid var(--wg-primary);
    background-color: #ffffff;

    &:disabled, &[disabled] {
      color: #747779;
      border: 1px solid #747779;
      background-color: #ffffff;
    }

    &:focus:not([disabled]),
    &:hover:not([disabled]) {
      outline-width: 2px;
      outline-style: solid;
      outline-color: var(--wg-blue-link);
    }
  `,
  transparent: `
    color: var(--wg-blue-link);
    background: transparent;
    border: 1px solid transparent;

    &:disabled, &[disabled] {
      color: #757575;
      background: transparent;
    }

    &:focus:not([disabled]),
    &:hover:not([disabled]) {
      outline-width: 2px;
      outline-style: solid;
      outline-color: var(--wg-blue-link);
    }
  `,
  warning: `
    color: #ffffff;
    background: var(--wg-red);
    --tw-border-opacity: 1;
    border: 1px solid;
    border-color: var(--wg-red);

     &:disabled, &[disabled] {
      color: #747779;
      border: 1px solid #747779;
      background-color: #ffffff;
    }

    &:hover:not([disabled]){
      --tw-bg-opacity: 1;
      background-color: rgb(155 28 28/var(--tw-bg-opacity))
    }

   &:focus:not([disabled]) {
    --tw-ring-offset-width: 2px;
    --tw-ring-opacity: 1;
    --tw-ring-color: #1a56dbb3;
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--wg-primary);
    box-shadow: var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow,0 0 #0000);

    }
    &:disabled, &.disabled {
      color: #ffffff;
      background: #9e837e;
      border: 1px solid #9e837e;
    }


  `
}

const StyledButton: any = styled.button.withConfig({
  shouldForwardProp: prop => !['size', 'color'].includes(prop)
})`
  cursor: pointer;
  min-height: ${({ size }: any) => (size === 'inline' ? 0 : 40)}px;
  margin: ${({ size }: any) => (size === 'small' ? '0' : size === 'inline' ? '0 0.1rem' : '0.5rem')};
  padding: ${({ size }: any) => (size === 'small' ? '0' : size === 'inline' ? 0 : '0.75rem 1.5rem')};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  align-items: center;
  display: inline-flex;
  text-decoration: ${({ size, color }: any) => (size === 'inline' && color === 'transparent' ? 'underline' : '')};
  ${({ color = 'primary' }) => cssPerColorProp[color]};

  &:disabled,
  &[disabled] {
    cursor: default;
  }

  svg {
    display: inline-block;
  }
`

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: 'primary' | 'secondary' | 'tertiary' | 'transparent' | 'warning'
  as?: 'button' | 'a' | Link
  ariaLabel?: string // Why isn't this part of the ButtonHTMLAttributes<HTMLButtonElement>... sigh TS
  size?: 'normal' | 'small' | 'inline'
  to?: any // Only needed if you use as `Link`
}

/**
 * A standard Button
 *
 * @example
 * <Button color="primary" onClick={() => {}}>Submit</Button>
 */
const Button = forwardRef(
  ({ children, color = 'primary', as = 'button', type = 'button', size = 'normal', ...rest }: ButtonProps, ref) => {
    return (
      <StyledButton className="flex space-x-2" as={as} color={color} type={type} size={size} {...rest} ref={ref}>
        {children}
      </StyledButton>
    )
  }
)

Button.displayName = 'Button'

export default Button
